"use client";

import type { PlainDate, PlainDateString } from "@sa-nu/shared-utils/date";
import {
  genPlainDateList,
  getCurrentDate,
  toPlainDateString,
} from "@sa-nu/shared-utils/date";
import { useEffect, useMemo, useState } from "react";

import {
  DatePicker,
  type DatePickerSelection,
} from "../../../dx/ui/date-picker";
import { DxIcon } from "../../../dx/ui/icons";
import { LoadingIndicator } from "../../../dx/ui/loading-indicator";
import { useRoomTypeReserveContext } from "../../_context/room-type-reserve-context";
import {
  applySearchCalendarCheckoutPhase,
  applySearchCalendarConfirmedPhase,
  type SearchCalendarDateInfo,
} from "../../_utils/build-search-calendar-date-info-map";
import { getInitialVisibleDate } from "../../_utils/get-initial-visible-date";
import { getSearchCalendarRenderRange } from "../../_utils/get-search-calendar-render-range";
import { FilterChip } from "../../ui/filter-chip";
import { TriggerField } from "../../ui/trigger-field";

import { useTriggerToggle } from "./_hooks/use-trigger-toggle";
import { formatDateRangeChipLabel } from "./_utils/format-date-range-chip-label";
import { FilterPopupShell } from "./filter-popup-shell";

/**
 * 滞在期間フィルター: サイドバーセクション（ラベル＋トリガー＋適用チップ＋ポップアップ）。
 *
 * Figma（5140-6188 日程popup / 5140-7675 カレンダー中身）準拠:
 * - ポップアップ内: DatePicker（縦スクロール全月表示）
 *
 * 状態モデル:
 * - ポップアップ内操作は draft selection をトグル（即時適用しない）。
 * - 開く度に draft を確定値（firstNight/lastNight）から再初期化。
 * - 決定: draft を適用（onConfirm）→ close。
 * - クリア（ポップアップ内）: draft を null/null にリセット → 未適用。
 * - チップ×: 即時適用（onClear）。
 * - セクションクリア: 即時適用（onClear）。
 */
export function DateFilterPopup({
  firstNight,
  lastNight,
  onConfirm,
  onClear,
  externalOpen,
  onExternalClose,
  variant = "filter-sidebar",
  externalAnchorEl,
  dateInfoMap,
  dateInfoMapLoading = false,
}: {
  firstNight: PlainDate | null;
  lastNight: PlainDate | null;
  onConfirm: (firstNight: PlainDate, lastNight: PlainDate) => void;
  onClear: () => void;
  /** SP 検索バーなど外部からポップアップを開く場合に true を渡す */
  externalOpen?: boolean;
  /** externalOpen で開いたポップアップが閉じられたときのコールバック */
  onExternalClose?: () => void;
  variant?: "filter-sidebar" | "filter-search";
  /**
   * 外部からアンカー要素を渡す場合に使用（SP 検索バーなど）。
   * 指定時は内部 anchorEl より優先される。
   */
  externalAnchorEl?: HTMLElement | null;
  /** 日付ごとの最低価格・在庫状況。未指定時は価格行・満室表示なしの従来通り */
  dateInfoMap?: Record<PlainDateString, SearchCalendarDateInfo>;
  /** dateInfoMap 取得中かどうか。true の間は DatePicker の代わりに LoadingIndicator を表示する */
  dateInfoMapLoading?: boolean;
}) {
  const ctx = useRoomTypeReserveContext();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftSelection, setDraftSelection] = useState<DatePickerSelection>({
    startDate: null,
    endDate: null,
  });

  const hasRange = firstNight !== null && lastNight !== null;

  // 表示: チェックイン〜チェックアウト（= lastNight + 1）
  const chipLabel =
    formatDateRangeChipLabel(firstNight, lastNight) ?? "日程を指定";

  const triggerLabel = hasRange ? "日程選択あり" : "滞在期間";

  const handleOpen = () => {
    // 開く度に確定値でドラフトを再初期化
    setDraftSelection({
      startDate: firstNight,
      endDate: lastNight !== null ? lastNight.add({ days: 1 }) : null,
    });
    setOpen(true);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // × / 外側クリック / Esc → ドラフト破棄
      setOpen(false);
      onExternalClose?.();
    }
  };

  // トリガー（外部 anchor）を開閉トグルにする（outside-press との競合を回避）
  const triggerToggle = useTriggerToggle(open, handleOpen, () =>
    setOpen(false),
  );

  // externalOpen が true になったら Drawer を開く（SP 検索バーからのトリガー）
  useEffect(() => {
    if (externalOpen) {
      handleOpen();
    } else if (externalOpen === false) {
      setOpen(false);
    }
    // handleOpen は firstNight/lastNight に依存するが、
    // externalOpen が変化したタイミングのみ実行すればよい
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen]);

  const handleClear = () => {
    setDraftSelection({ startDate: null, endDate: null });
  };

  const handleConfirm = () => {
    const { startDate, endDate } = draftSelection;
    if (startDate !== null && endDate !== null) {
      // endDate in DatePicker = checkout day = lastNight + 1
      onConfirm(startDate, endDate.subtract({ days: 1 }));
    } else if (startDate === null && endDate === null) {
      // クリア後の決定 → 日程フィルター解除
      onClear();
    } else {
      return; // 片側のみ選択（範囲未確定）は確定しない
    }
    setOpen(false);
  };

  const today = getCurrentDate();
  const { minDate, maxDate, renderMaxDate } =
    getSearchCalendarRenderRange(today);
  // CO 日の最遠端（maxDate+1）も選べるよう +1
  const windowNights = minDate.until(maxDate, { largestUnit: "day" }).days + 1;
  const maxNights =
    ctx?.maxStayNights != null
      ? Math.min(windowNights, ctx.maxStayNights)
      : windowNights;

  // 選択可能な上限。チェックインは maxDate(最終泊)まで。CO 選択中だけ +1 して maxDate+1 を
  // CO として選べるようにする（CO 確定後も renderMaxDate と選択済み表示で maxDate+1 は見え続ける）。
  const selectableMaxDate =
    draftSelection.startDate !== null && draftSelection.endDate === null
      ? maxDate.add({ days: 1 })
      : maxDate;

  // フェーズごとに baseMap（dateInfoMap）を上書きする:
  // - CI 選択済み・CO 未選択: CO 候補日を連続性判定で上書き
  // - CI/CO 確定済み: CO 日（宿泊しない日）の price を上書き
  const effectiveDateInfoMap = useMemo(() => {
    if (dateInfoMap === undefined) return undefined;
    if (draftSelection.startDate !== null && draftSelection.endDate !== null) {
      return applySearchCalendarConfirmedPhase(
        dateInfoMap,
        draftSelection.endDate,
      );
    }
    if (draftSelection.startDate === null) {
      return dateInfoMap;
    }
    const calendarDates = genPlainDateList(minDate, renderMaxDate).map(
      toPlainDateString,
    );
    return applySearchCalendarCheckoutPhase(
      dateInfoMap,
      calendarDates,
      draftSelection.startDate,
      ctx?.maxStayNights ?? null,
    );
  }, [
    dateInfoMap,
    draftSelection.startDate,
    draftSelection.endDate,
    minDate,
    renderMaxDate,
    ctx?.maxStayNights,
  ]);

  const bothNull =
    draftSelection.startDate === null && draftSelection.endDate === null;
  const bothSet =
    draftSelection.startDate !== null && draftSelection.endDate !== null;
  // クリア後（bothNull）も「日程指定なし」を決定で適用できるようにする。
  // 片側だけ選択（範囲未確定）は決定不可。
  const canConfirm = bothNull || bothSet;
  const canClearDraft =
    draftSelection.startDate !== null || draftSelection.endDate !== null;

  return (
    <div className="flex flex-col gap-[8px]">
      <p className="font-notojp text-dx-text-default text-[14px] leading-[24px] font-bold">
        滞在期間
      </p>

      {/* ref を callback ref で取得（AreaFilterPopup と同じ作法） */}
      <div
        ref={(el) => setAnchorEl(el)}
        onPointerDown={triggerToggle.onPointerDown}
      >
        <TriggerField
          icon={
            <DxIcon src="/i/dx/calendar.svg" className="text-dx-text-default" />
          }
          label={triggerLabel}
          hasValue={hasRange}
          onClick={triggerToggle.onClick}
          data-analytics-id="room_type_browse_opened_date_filter"
        />
      </div>

      {hasRange && (
        <div className="flex items-center gap-[8px] px-[16px] py-[8px]">
          <div className="flex flex-wrap gap-[8px]">
            <FilterChip label={chipLabel} onRemove={onClear} />
          </div>
          <button
            data-analytics-id="room_type_browse_clicked_date_clear"
            type="button"
            onClick={onClear}
            className="font-notojp text-dx-text-default focus-visible:ring-dx-border-focus shrink-0 rounded-sm text-[12px] leading-none underline focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            クリア
          </button>
        </div>
      )}

      {/* 滞在期間ポップアップ */}
      <FilterPopupShell
        open={open}
        onOpenChange={handleOpenChange}
        anchorEl={externalAnchorEl ?? anchorEl}
        title="滞在期間を選択"
        canClear={canClearDraft}
        canConfirm={canConfirm}
        onClear={handleClear}
        onConfirm={handleConfirm}
        variant={variant}
      >
        {/* DatePicker body: flex-1 で高さを埋める */}
        <div className="relative -mx-[12px] min-h-0 flex-1">
          {dateInfoMapLoading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingIndicator label="空き状況を読み込み中…" />
            </div>
          ) : (
            <div className="absolute inset-0">
              <DatePicker
                minDate={minDate}
                maxDate={selectableMaxDate}
                renderMaxDate={renderMaxDate}
                startDate={draftSelection.startDate}
                endDate={draftSelection.endDate}
                initialVisibleDate={getInitialVisibleDate(today)}
                onChange={setDraftSelection}
                today={today}
                maxNights={maxNights}
                dateInfoMap={effectiveDateInfoMap}
                size="small"
                unavailablePeriods={ctx?.unavailablePeriods}
              />
            </div>
          )}
        </div>
      </FilterPopupShell>
    </div>
  );
}
